import { useState } from "react";

import { MenuItem, SelectChangeEvent, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { updateBotLinkedDcaBot } from "API/calls";
import BackdropLoading from "components/elements/BackdropLoading";
import Modal from "components/elements/Modal";
import ResponsiveLabel from "components/elements/ResponsiveLabel";
import Select from "components/elements/Select";
import { useAPIDcaBotsList, useAPIExchangesMetadata } from "hooks/useAPI";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { LinkedDcaMode } from "types/enums";
import { IDcaBotDetail } from "types/types";
import { isCoinmateGridCurrencyPair } from "utils";

interface IProps {
  id: string;
  handleClose: () => void;
  currencyPair: string;
  refreshData: () => void;
  initialLinkedDcaBotId?: string;
}

const getLinkedDcaBotLabel = (bot: IDcaBotDetail) =>
  [bot.exchangeConnection.exchangeTitle, bot.currencyPair, bot.label].filter(Boolean).join(" ");

const LinkModal = ({ id, handleClose, currencyPair, refreshData, initialLinkedDcaBotId }: IProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: dcaBotsData } = useAPIDcaBotsList();
  const { data: exchangesMetadata } = useAPIExchangesMetadata();
  const [isProcessing, setIsProcessing] = useState(false);
  const [linkedDcaMode, setLinkedDcaMode] = useState<LinkedDcaMode>(LinkedDcaMode.LINK);
  const [linkedDcaBotId, setLinkedDcaBotId] = useState(initialLinkedDcaBotId ?? "");

  const isEligibleLinkedDcaBot = (bot: IDcaBotDetail) =>
    bot.currencyPair === currencyPair && isCoinmateGridCurrencyPair(exchangesMetadata, bot.currencyPair);

  const handleChangeLinkedDcaBot = (event: SelectChangeEvent<unknown>) => {
    setLinkedDcaBotId(String(event.target.value));
  };

  const selectedLinkedDcaBotId = linkedDcaMode === LinkedDcaMode.NONE ? null : linkedDcaBotId || null;
  const hasChange = selectedLinkedDcaBotId !== (initialLinkedDcaBotId || null);
  const canConfirm = hasChange && (linkedDcaMode === LinkedDcaMode.NONE || Boolean(linkedDcaBotId));

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      await updateBotLinkedDcaBot(id, selectedLinkedDcaBotId);
      refreshData();
      handleClose();
      enqueueSnackbar(t("gridBot.linkedDcaStrategy.saveSuccess"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("gridBot.linkedDcaStrategy.saveError"), { variant: "error" });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      open
      onCancel={handleClose}
      title={t("gridBot.linkedDcaStrategy.modalTitle")}
      cancelTitle={t("cancel")}
      onConfirm={handleConfirm}
      disableConfirmButton={isProcessing || !canConfirm}
      size="sm"
      disableCloseOnClickOutside
      withCloseButton>
      <Stack spacing={2}>
        <Typography variant="caption" fontWeight={400}>
          <Trans i18nKey="gridBot.linkedDcaStrategy.info1" />
        </Typography>
        <ToggleButtonGroup
          color="primary"
          size="medium"
          exclusive
          value={linkedDcaMode}
          onChange={(_event, value: LinkedDcaMode | null) => {
            if (value === null) return;
            setLinkedDcaMode(value);
          }}>
          <ToggleButton sx={{ textTransform: "none" }} value={LinkedDcaMode.NONE}>
            <Trans i18nKey="gridBot.linkedDcaStrategy.modeNone" />
          </ToggleButton>
          <ToggleButton sx={{ textTransform: "none" }} value={LinkedDcaMode.LINK}>
            <Trans i18nKey="gridBot.linkedDcaStrategy.modeLink" />
          </ToggleButton>
        </ToggleButtonGroup>
        {linkedDcaMode === LinkedDcaMode.LINK && (
          <>
            <Typography variant="caption" fontWeight={400}>
              <Trans i18nKey="gridBot.linkedDcaStrategy.info2" />
            </Typography>
            <Select
              id="linkedDcaBotId"
              name="linkedDcaBotId"
              label={ResponsiveLabel(t("gridBot.linkedDcaStrategy.select"))}
              value={linkedDcaBotId}
              onChange={handleChangeLinkedDcaBot}
              disabled={!dcaBotsData?.dcaBots.length}>
              {dcaBotsData?.dcaBots.map((bot) => (
                <MenuItem key={bot.id} value={bot.id} disabled={!isEligibleLinkedDcaBot(bot)}>
                  {ResponsiveLabel(getLinkedDcaBotLabel(bot))}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      </Stack>
      <BackdropLoading open={isProcessing} />
    </Modal>
  );
};

export default LinkModal;
